import { EmailMessageModel, parseEmailConvId } from "@jugl-web/rest-api";
import { apiUTCToLocalDateTime, cx, useTranslations } from "@jugl-web/utils";
import { ARIVU_SYSTEM_CHAT_USER_ID } from "@jugl-web/utils/consts";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import { useUserGeneralProfile } from "@jugl-web/domain-resources/users/hooks/useUserGeneralProfile";
import { format } from "date-fns";
import React, { useMemo } from "react";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { emailMessageToAttachmentsPayload } from "@web-src/modules/client-support/utils/emailMessageToAttachmentsPayload";
import { CRMInfoMessage } from "../CRMInfoMessage";
import { useEmailAccount } from "../../../../providers/EmailAccountProvider/useEmailAccount";
import { EmailAvatar } from "../../../EmailAvatar";
import aiAgentIcon from "../../assets/ai-agent.svg?url";
import { EmailMessageAttachments } from "./components/EmailMessageAttachments";
import { EmailMessageBody } from "./components/EmailMessageBody";

// parses an address like "John Doe <john@example.com>" into its parts
const parseEmailAddress = (raw?: string): { name?: string; email?: string } => {
  if (!raw) return {};

  const match = raw.match(/^(.*?)\s*<([^>]+)>$/);
  if (match) {
    return { name: match[1].trim() || undefined, email: match[2] };
  }

  return { email: raw.trim() };
};

export const EmailMessageItem: React.FC<{
  message: EmailMessageModel;
}> = ({ message }) => {
  const { emailAccount } = useEmailAccount();
  const { entityId } = useEntitySelectedProvider();
  const { dateLocale } = useLanguage();
  const { t } = useTranslations();

  const isOutgoing = message.type === "outgoing";
  // outgoing emails sent by the AI auto-responder carry the sentinel `from` id
  // instead of a real user id
  const isAiAgent = isOutgoing && message.from === ARIVU_SYSTEM_CHAT_USER_ID;

  // outgoing emails are sent by a Jugl user; resolve their name/avatar from the
  // user profile rather than the connected email account
  const { profile: senderProfile, safeProfile: senderSafeProfile } =
    useUserGeneralProfile({
      entityId,
      userId: message.from,
      skip: !isOutgoing || isAiAgent,
    });

  const attachments = useMemo(
    () => emailMessageToAttachmentsPayload({ message, entityId }),
    [message, entityId]
  );

  const { senderName, senderEmail, recipientEmail } = useMemo(() => {
    const counterpartEmail = parseEmailConvId(message.conv_id).email;

    if (isAiAgent) {
      return {
        senderName: t({
          id: "client-support-page.ai-agent",
          defaultMessage: "AI Agent",
        }),
        senderEmail: emailAccount?.email, // AI sends from the connected account
        recipientEmail: counterpartEmail,
      };
    }

    if (isOutgoing) {
      return {
        senderName: senderSafeProfile.displayName,
        senderEmail: emailAccount?.email,
        recipientEmail: counterpartEmail,
      };
    }

    const from = parseEmailAddress(message.payload.from);
    const to = parseEmailAddress(message.payload.to);

    return {
      senderName: from.name || from.email || counterpartEmail,
      senderEmail: from.email,
      recipientEmail: to.email || emailAccount?.email,
    };
  }, [
    emailAccount?.email,
    isAiAgent,
    isOutgoing,
    message,
    senderSafeProfile.displayName,
    t,
  ]);

  const formattedTimestamp = useMemo(
    () =>
      format(
        apiUTCToLocalDateTime(message.payload.timestamp),
        "MMM d, yyyy, h:mm a",
        { locale: dateLocale }
      ),
    [dateLocale, message.payload.timestamp]
  );

  if (message.type === "info") {
    return (
      <CRMInfoMessage
        message={message.payload.text}
        timestamp={message.payload.timestamp}
      />
    );
  }

  return (
    <div
      className={cx(
        "my-3 w-fit max-w-[748px] min-w-[300px] rounded-2xl bg-white",
        isOutgoing
          ? "mr-8 ml-auto border border-solid border-dark-100 shadow-[0_4px_12px_0_rgba(0,0,0,0.06)]"
          : "mr-auto ml-8"
      )}
    >
      <div className="flex items-start justify-between gap-3 px-6 pt-5">
        <div className="flex min-w-0 items-center gap-3">
          <EmailAvatar
            username={senderName}
            imageUrl={
              isAiAgent
                ? aiAgentIcon
                : isOutgoing
                  ? senderProfile?.avatar
                  : undefined
            }
            fallbackToInitials={isOutgoing && !isAiAgent}
            className="h-9 w-9"
          />
          <div className="flex w-full flex-col gap-0.5 overflow-hidden">
            <span className="truncate font-secondary text-sm text-dark-800">
              <span
                className={cx("font-medium", !isOutgoing && "text-[#2F80ED]")}
              >
                {senderName}
              </span>
              {senderEmail && senderEmail !== senderName && (
                <span className="text-[#828282]">{` <${senderEmail}>`}</span>
              )}
            </span>
            {recipientEmail && (
              <span className="truncate font-secondary text-xs text-[#828282]">
                To: {recipientEmail}
              </span>
            )}
          </div>
        </div>
        <span className="shrink-0 truncate font-secondary text-xs text-[#828282]">
          {formattedTimestamp}
        </span>
      </div>
      <div className="mx-6 my-4 h-px bg-dark-100" />
      {message.payload.text.trim() && (
        <EmailMessageBody messageId={message.id} text={message.payload.text} />
      )}
      {attachments?.length ? (
        <EmailMessageAttachments attachments={attachments} />
      ) : null}
    </div>
  );
};
